import { useMutation } from "@apollo/client/react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  ClaspPushDataSourceGSheetsDocument,
  type ClaspPushDataSourceGSheetsMutation,
  type ClaspPushDataSourceGSheetsMutationVariables,
  InvokeApplySchemaDocument,
  type InvokeApplySchemaMutation,
  type InvokeApplySchemaMutationVariables,
} from "~/features/dataImportProjects/gqls";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";
import { useActionWithConfirm } from "~/lib/handleActionWithConfirm";

type DataSource = {
  id: string;
  name: string;
};

type UseDataSourceActionsParams = {
  canExecute?: () => boolean;
};

export const useDataSourceActions = ({ canExecute }: UseDataSourceActionsParams = {}) => {
  const { t } = useTranslation();
  const handleActionWithConfirm = useActionWithConfirm();
  const [claspPushDataSourceGSheets] = useMutation<
    ClaspPushDataSourceGSheetsMutation,
    ClaspPushDataSourceGSheetsMutationVariables
  >(ClaspPushDataSourceGSheetsDocument);
  const [invokeApplySchema] = useMutation<InvokeApplySchemaMutation, InvokeApplySchemaMutationVariables>(
    InvokeApplySchemaDocument,
  );
  const { tenantId } = useTenant();
  const onClaspPushClick = useCallback(
    (dataSource: DataSource) => {
      if (!tenantId) {
        toast.error(translateError(t, new Error("Tenant is not resolved")));
        return;
      }

      handleActionWithConfirm({
        confirmMessage: t("feature.dataImportProjects.detail.dataSource.confirm.claspPush", {
          dataSourceName: dataSource.name,
        }),
        successMessage: t("feature.dataImportProjects.detail.dataSource.alert.claspPushSuccess"),
        action: () =>
          claspPushDataSourceGSheets({
            variables: { input: { tenantId, id: dataSource.id } },
          }),
        canExecute,
      });
    },
    [handleActionWithConfirm, t, claspPushDataSourceGSheets, tenantId, canExecute],
  );

  const onApplySchemaClick = useCallback(
    (dataSource: DataSource, { googleSpreadsheetId }: { googleSpreadsheetId: string }) => {
      if (!tenantId) {
        toast.error(translateError(t, new Error("Tenant is not resolved")));
        return;
      }

      handleActionWithConfirm({
        confirmMessage: t("feature.dataImportProjects.detail.dataSource.confirm.applySchema", {
          dataSourceName: dataSource.name,
        }),
        successMessage: t("feature.dataImportProjects.detail.dataSource.alert.applySchemaSuccess"),
        action: () =>
          invokeApplySchema({
            variables: { input: { tenantId, spreadsheetId: googleSpreadsheetId } },
          }),
        canExecute,
      });
    },
    [handleActionWithConfirm, t, invokeApplySchema, tenantId, canExecute],
  );

  return {
    onClaspPushClick,
    onApplySchemaClick,
  };
};
